import React, { useState, useEffect } from "react";
import {
    Autocomplete,
    TextField,
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import PeopleIcon from "@mui/icons-material/People";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";
import ReponedoresService from "../../api/reponedores.api";

// Formulario de asignación de reponedores a almacenes
const ReponedorForm = ({ reponedor, almacenesOptions, usuariosOptions, onSave, onError, onCancel }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [formData, setFormData] = useState({
        almacenes: [],
        usuarios: []
    });

    const [guardando, setGuardando] = useState(false);

    // Cargar datos del reponedor cuando se pasa para editar
    useEffect(() => {
        if (reponedor) {
            setFormData({
                almacenes: reponedor.almacenes ?? [],
                usuarios: reponedor.usuarios ?? []
            });
        } else {
            // Resetear formulario si no hay reponedor (modo crear)
            setFormData({
                almacenes: [],
                usuarios: []
            });
        }
    }, [reponedor]);

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGuardar = async () => {
        setGuardando(true);
        try {
            const reponedorData = {
                almacenes: formData.almacenes,
                usuarios: formData.usuarios
            };

            let resultado;
            if (reponedor?.id) {
                resultado = await ReponedoresService.update(reponedor.id, reponedorData);
            } else {
                resultado = await ReponedoresService.create(reponedorData);
            }

            if (onSave) {
                onSave(resultado, reponedor?.id ? 'updated' : 'created');
            }

            // Resetear formulario después de guardar
            setFormData({
                almacenes: [],
                usuarios: []
            });
        } catch (error) {
            if (onError) {
                onError(error.response?.data?.message || error.message);
            }
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = () => {
        if (onCancel) {
            onCancel();
        }
        // Resetear formulario
        setFormData({
            almacenes: [],
            usuarios: []
        });
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Card
                sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                mb: 0.5,
                            }}
                        >
                            {reponedor?.id ? 'Editar asignación' : 'Nueva asignación'}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#6c757d",
                            }}
                        >
                            {reponedor?.id
                                ? 'Modifica los datos de la asignación'
                                : 'Asigna usuarios reponedores a almacenes'}
                        </Typography>
                    </Box>

                    <Stack spacing={3}>
                        {/* Almacenes */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <StoreIcon sx={{ fontSize: 18 }} />
                                Almacenes
                            </Typography>
                            <Autocomplete
                                multiple
                                options={almacenesOptions}
                                getOptionLabel={(option) => option.label}
                                value={almacenesOptions.filter(opt => formData.almacenes.includes(opt.value))}
                                onChange={(e, newValue) => {
                                    handleFormChange('almacenes', newValue.map(v => v.value));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Seleccione almacenes"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#fff",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Usuarios */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <PeopleIcon sx={{ fontSize: 18 }} />
                                Usuarios reponedores
                            </Typography>
                            <Autocomplete
                                multiple
                                options={usuariosOptions}
                                getOptionLabel={(option) => option.label}
                                value={usuariosOptions.filter(opt => formData.usuarios.includes(opt.value))}
                                onChange={(e, newValue) => {
                                    handleFormChange('usuarios', newValue.map(v => v.value));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Seleccione usuarios"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#fff",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Botones de acción */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            sx={{ pt: 2 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={guardando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
                                onClick={handleGuardar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                {guardando ? 'Guardando...' : (reponedor?.id ? 'Actualizar' : 'Guardar asignación')}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_SECUNDARIO_SX}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ReponedorForm;
